import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { errorService, ErrorService } from '../services/errorService';

interface UseErrorHandlerOptions {
  showToast?: boolean;
  logError?: boolean;
  redirectTo?: string;
  fallbackMessage?: string;
}

export const useErrorHandler = (
  toastRef?: React.RefObject<Toast | null>,
  defaultOptions?: UseErrorHandlerOptions
) => {
  const navigate = useNavigate();

  // Callers pass an object literal, so `defaultOptions` is a new reference on
  // every render. Holding it in a ref keeps it out of the useCallback dep array,
  // which is what stops handleError/handleAsyncOperation from changing identity
  // each render and re-firing every useEffect that depends on them.
  const defaultOptionsRef = useRef(defaultOptions);
  defaultOptionsRef.current = defaultOptions;

  // Redirect timers, cleared on unmount so a queued navigate() cannot fire
  // against an unmounted component.
  const redirectTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const timers = redirectTimers;
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, []);

  const handleError = useCallback(
    async (
      error: unknown,
      options?: UseErrorHandlerOptions
    ) => {
      const opts = {
        showToast: true,
        logError: true,
        ...defaultOptionsRef.current,
        ...options
      };

      // Get user-friendly error message
      const userMessage = opts.fallbackMessage || ErrorService.getUserFriendlyMessage(error);

      // Log error if enabled
      if (opts.logError) {
        await errorService.logError(
          error instanceof Error ? error : new Error(String(error)),
          undefined,
          'medium',
          {
            component: 'useErrorHandler',
            showToast: opts.showToast,
            logError: opts.logError,
            redirectTo: opts.redirectTo || null,
            fallbackMessage: opts.fallbackMessage || null
          }
        );
      }

      // Show toast if enabled and ref is provided
      if (opts.showToast && toastRef?.current) {
        toastRef.current.show({
          severity: 'error',
          summary: 'Error',
          detail: userMessage,
          life: 5000
        });
      }

      // Redirect if specified
      if (opts.redirectTo) {
        const destination = opts.redirectTo;
        const timer = setTimeout(() => {
          redirectTimers.current = redirectTimers.current.filter(t => t !== timer);
          navigate(destination);
        }, 1000);
        redirectTimers.current.push(timer);
      }

      return userMessage;
    },
    [navigate, toastRef]
  );

  const handleAsyncOperation = useCallback(
    async <T,>(
      operation: () => Promise<T>,
      options?: UseErrorHandlerOptions
    ): Promise<T | null> => {
      try {
        return await operation();
      } catch (error) {
        await handleError(error, options);
        return null;
      }
    },
    [handleError]
  );

  return {
    handleError,
    handleAsyncOperation
  };
};
